import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import api, { formatApiError } from "@/lib/api";
import { useLocale } from "@/contexts/LocaleContext";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import { ChevronLeft, ThumbsUp, ThumbsDown, Eye } from "lucide-react";

export default function HelpKbArticle() {
  const { slug } = useParams();
  const { formatDate } = useLocale();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/kb/articles/${slug}`);
        setArticle(data);
      } catch (e) { toast.error(formatApiError(e)); }
      finally { setLoading(false); }
    })();
  }, [slug]);

  const sendFeedback = async (helpful) => {
    setFeedback(helpful);
    try {
      await api.post(`/kb/articles/${slug}/feedback`, { helpful });
      toast.success("Thanks for the feedback");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (loading) return <AppShell title="Article"><div className="max-w-4xl mx-auto py-8 px-4 text-xs text-navy-400">Loading…</div></AppShell>;
  if (!article) return <AppShell title="Article"><div className="max-w-4xl mx-auto py-8 px-4 text-sm text-navy-500">Article not found.</div></AppShell>;

  return (
    <AppShell title={article.title}>
      <div className="max-w-5xl mx-auto py-8 px-4 grid grid-cols-1 md:grid-cols-[1fr_240px] gap-8">
        <div>
          <Link to="/help/kb" className="inline-flex items-center text-sm text-navy-500 hover:text-navy-900 mb-3" data-testid="kb-article-back">
            <ChevronLeft className="w-4 h-4 mr-1" /> Knowledge base
          </Link>
          <h1 className="text-3xl font-semibold mb-2" data-testid="kb-article-heading">{article.title}</h1>
          <div className="text-xs text-navy-500 mb-6 flex items-center gap-3">
            <span>{article.published_at ? formatDate(article.published_at) : "—"}</span>
            <span className="inline-flex items-center gap-1"><Eye className="w-3 h-3" /> {article.views} views</span>
            {article.tags?.length > 0 && (
              <span>{article.tags.map((t) => `#${t}`).join(" ")}</span>
            )}
          </div>
          <Markdown text={article.body_markdown} className="prose-eb" />
          <div className="eb-card mt-8 p-4 flex items-center justify-between" data-testid="kb-feedback">
            <span className="text-sm text-navy-600">Was this helpful?</span>
            <div className="flex gap-2">
              <button
                onClick={() => sendFeedback(true)}
                disabled={feedback !== null}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border ${feedback === true ? "bg-green-50 border-green-300 text-green-700" : "border-navy-200 hover:bg-navy-50"}`}
                data-testid="kb-feedback-yes"
              >
                <ThumbsUp className="w-3.5 h-3.5" /> Yes
              </button>
              <button
                onClick={() => sendFeedback(false)}
                disabled={feedback !== null}
                className={`flex items-center gap-1 px-3 py-1.5 rounded-md text-sm border ${feedback === false ? "bg-red-50 border-red-300 text-red-700" : "border-navy-200 hover:bg-navy-50"}`}
                data-testid="kb-feedback-no"
              >
                <ThumbsDown className="w-3.5 h-3.5" /> No
              </button>
            </div>
          </div>
        </div>
        <aside className="eb-card p-4 h-fit" data-testid="kb-article-related">
          <div className="text-xs uppercase tracking-wider text-navy-500 font-semibold mb-3">Related</div>
          {(article.related || []).length === 0 ? (
            <div className="text-xs text-navy-400">No related articles.</div>
          ) : (
            <ul className="space-y-2">
              {article.related.map((r) => (
                <li key={r.id}>
                  <Link to={`/help/kb/${r.slug}`} className="text-sm text-navy-700 hover:text-orange-600" data-testid={`kb-related-${r.slug}`}>
                    {r.title}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </aside>
      </div>
    </AppShell>
  );
}
